import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useLocation, matchPath } from 'react-router-dom';
import { Box, Drawer, Hidden, Link, List, ListSubheader } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import HomeIcon from '@mui/icons-material/Home';
import RoomPreferencesIcon from '@mui/icons-material/RoomPreferences';
import PeopleIcon from '@mui/icons-material/People';
import EngineeringIcon from '@mui/icons-material/Engineering';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import HistoryIcon from '@mui/icons-material/History';
import { FormattedMessage } from 'react-intl';

// https://github.com/goldenyz/react-perfect-scrollbar
import PerfectScrollbar from 'react-perfect-scrollbar';

import Logo from 'src/components/Logo';
import NavItem from './NavItem';
import useSettings from 'src/hooks/useSettings';

const sections = [
  {
    items: [
      {
        title: 'Home',
        icon: HomeIcon,
        href: '/app/home'
      },
      {
        title: (
          <FormattedMessage
            defaultMessage="権限グループ"
            id="NavBar.Button.AuthorizationGroup"
          />
        ),
        icon: RoomPreferencesIcon,
        href: '/app/auth_groups'
      },
      {
        title: (
          <FormattedMessage
            defaultMessage="入館者"
            id="NavBar.Button.AdmissionPeople"
          />
        ),
        icon: PeopleIcon,
        href: '/app/admission_people'
      },
      {
        title: (
          <FormattedMessage
            defaultMessage="搬入出"
            id="NavBar.Button.CarryRequest"
          />
        ),
        icon: EmojiTransportationIcon,
        href: '/app/carry_requests'
      },
      {
        title: (
          <FormattedMessage
            defaultMessage="MDF入室管理"
            id="NavBar.Button.MdfAdmissionRequest"
          />
        ),
        icon: EngineeringIcon,
        href: '/app/mdf_admission_requests'
      },
      {
        title: (
          <FormattedMessage
            defaultMessage="入館受付履歴"
            id="NavBar.Button.AdmissionHistory"
          />
        ),
        icon: HistoryIcon,
        href: '/app/history/admission_histories'
      }
    ]
  }
];

function renderNavItems({
  items,
  pathname,
  openNavItem,
  closeNavItem,
  stateNavItem,
  depth = 0
}) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) =>
          reduceChildRoutes({
            acc,
            item,
            pathname,
            openNavItem,
            closeNavItem,
            stateNavItem,
            depth
          }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  openNavItem,
  closeNavItem,
  stateNavItem,
  depth
}) {
  const key =
    typeof item.title === 'object' && item.title.props
      ? item.title.props.id
      : item.title + depth;

  if (item.items) {
    /*
      matchPathの詳細は: https://reactrouter.com/web/api/matchPath
      pathnameが /app/management/customers の場合は下記となる
      {
        isExact: true, pathnameとitem.hrefが同一なら指定したexactを無視してtrueになる
        params: {},
        path: "/app/management/customers",
        url: "/app/management/customers",

      }
    */
    let open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    if (stateNavItem.openItem.length > 0) {
      open = matchPath(stateNavItem.openItem, {
        path: item.href,
        exact: false
      });
    }

    if (stateNavItem.closeItem.length > 0) {
      if (stateNavItem.closeItem === item.href) {
        open = false;
      } else {
        open = matchPath(stateNavItem.closeItem, {
          path: item.href,
          exact: false
        });
      }
    }

    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        open={Boolean(open)}
        title={item.title}
        key={key}
        openNavItem={(href) => openNavItem(href)}
        closeNavItem={(href) => closeNavItem(href)}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          openNavItem: openNavItem,
          closeNavItem: closeNavItem,
          stateNavItem: stateNavItem
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles((theme) => ({
  mobileDrawer: {
    width: 256,
    backgroundColor: theme.palette.background.default,
    border: 'none'
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)',
    backgroundColor: theme.palette.background.default
  },
  logo: {
    padding: '16px',
    color: theme.palette.background.dark
  },
  subheader: {
    color: theme.palette.background.dark,
    paddingLeft: theme.spacing(2)
  }
}));

const NavBar = ({ openDesktop, onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { settings } = useSettings();

  const [stateNavItem, setStateNavItem] = useState({
    openItem: '',
    closeItem: ''
  });

  const openNavItem = (href) => {
    setStateNavItem({
      ...stateNavItem,
      openItem: href,
      closeItem: ''
    });
  };

  const closeNavItem = (href) => {
    setStateNavItem({
      ...stateNavItem,
      openItem: '',
      closeItem: href
    });
  };

  /*
    下記をすることで、Mobile用ナビゲーションを使って他ページに遷移した後に、
    Mobile用ナビゲーションが自動的に消えるようになる
  */
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden mdUp>
          <Box display="flex" justifyContent="center">
            <Link
              className={classes.logo}
              component={RouterLink}
              to="/"
              underline="none"
            >
              <Logo appName={settings.appName} variant="h4" />
            </Link>
          </Box>
        </Hidden>
        <Box py={2}>
          {sections.map((section, index) => (
            <List
              key={index}
              subheader={
                <ListSubheader
                  disableGutters
                  disableSticky
                  className={classes.subheader}
                >
                  {section.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname,
                openNavItem: openNavItem,
                closeNavItem: closeNavItem,
                stateNavItem: stateNavItem
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden mdUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open={openDesktop}
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  openDesktop: PropTypes.bool,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
